import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { Box, Tooltip, useTheme } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import React, { useEffect, useRef, useState } from 'react'
import { LuX } from 'react-icons/lu'

import CustomChip from '@/components/Chip'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import BrandValue from '@/components/konva/components/BrandValue'
import LabelValuePair from '@/components/konva/components/LabelValuePair'
import { useAppSelector } from '@/hooks/redux'
import { PLACEMENT } from '@/store/appSlice'
import { getTooltipPlacement } from '@/utils/helper'

import { findBackgroundByCrownKey, getInitials } from '../utils'

const colors = ['#68CEA1', '#FFB975']
interface KonvaCrownProps {
  input: any
  output?: any
  onDelete: (crownKey: string) => void
  tooltipPlacement?: PLACEMENT
}

let lastTooltipMap: Record<string, { x: number; y: number }> = {}

const TooltipContent = ({
  output,
  setTooltipOpen,
}: {
  output: any
  setTooltipOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box
      display="flex"
      flexGrow={1}
      flexDirection="column"
      gap={1}
      alignItems="flex"
    >
      {expanded && (
        <>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
            flexGrow={1}
          >
            <LabelWithTooltip
              titleSx={{ fontSize: '14px', whiteSpace: 'nowrap' }}
              tooltipSx={{ height: '16px', width: '16px' }}
              label="Staining Target in LAB"
              tooltipText="Apply the staining recipe on the framework and measure before baking it with Optishade to verify the staining target in LAB values. The Chroma ( a*+b*) should be very close to the target. The Luminosity will be higher before baking it because it will drop after firing it."
            />
            <Icon icon={LuX} size={18} onClick={() => setTooltipOpen(false)} />
            <CloseFullscreenIcon
              onClick={() => setExpanded((prev) => !prev)}
              sx={{ height: 16, width: 16, cursor: 'pointer' }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexGrow={1}
          >
            <LabelValuePair label="L:" value={output?.LABafterstaining?.[0]} />
            <LabelValuePair label="a:" value={output?.LABafterstaining?.[1]} />
            <LabelValuePair label="b:" value={output?.LABafterstaining?.[2]} />
          </Box>
        </>
      )}
      {output?.provided_input?.stainingbrand && (
        <>
          {expanded && (
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          )}
          <BrandValue
            value={output.provided_input.stainingbrand || ''}
            expanded={expanded}
            setExpanded={setExpanded}
            setTooltipOpen={setTooltipOpen}
          />
        </>
      )}
      {expanded && output?.stainingrecipe?.stainingthickness && (
        <>
          <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          <LabelValuePair
            label="Staining Intensity"
            value={`${output?.stainingrecipe?.stainingthickness?.toFixed(2)}mm`}
          />
        </>
      )}
      {output?.stainingrecipe?.powders?.length && (
        <>
          <Box sx={{ borderTop: '1px solid #999', width: '100%' }}></Box>
          {output.stainingrecipe.powders.map((p, i) => (
            <LabelValuePair
              key={i}
              label={
                <Box
                  height="16px"
                  width="16px"
                  borderRadius={50}
                  bgcolor={colors?.[i] || '#68cea1'}
                />
              }
              value={`${output.stainingrecipe.ratios?.[i]} - ${p} `}
            />
          ))}
        </>
      )}
    </Box>
  )
}

const KonvaCrown: React.FC<KonvaCrownProps> = ({
  input,
  output,
  onDelete,
  tooltipPlacement,
}) => {
  const theme = useTheme()
  const [tooltipOpen, setTooltipOpen] = useState(true)
  const bgColor = findBackgroundByCrownKey(input?.fullmonolithiceffect)
  const [placement, setPlacement] = useState<'right' | 'left'>('right')

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const prevOutputRef = useRef<any>(null)

  useEffect(() => {
    const currentX = input?.x
    const currentY = input?.y
    const keyName = input?.fullmonolithiceffect

    const isTargetRoute =
      location.pathname === '/full-layering/staining-corrections'

    if (isTargetRoute && keyName === 'Incisal') {
      setPlacement('right')
      return
    }

    const placementResult = getTooltipPlacement({
      keyName,
      currentX,
      currentY,
      open: tooltipOpen,
      canvasImageId: canvasImage?.id,
      output,
      prevOutputRef,
      lastTooltipMap,
    })

    if (placementResult) {
      setPlacement(placementResult)
    }
  }, [
    tooltipOpen,
    input?.x,
    input?.y,
    output,
    canvasImage?.id,
    input?.fullmonolithiceffect,
    location.pathname,
  ])
  const labelPadding = !isEmpty(output) ? '0 8px 0 0px' : '0'
  return (
    <HtmlKonvaElement x={input.x} y={input.y} placement={tooltipPlacement}>
      <Box position="relative">
        <Box sx={{ transform: 'translate(-50%, -60%)' }}>
          <Tooltip
            open={tooltipOpen}
            title={
              isEmpty(input?.targetcolor) ? (
                <TooltipContent
                  output={output}
                  setTooltipOpen={setTooltipOpen}
                />
              ) : null
            }
            arrow
            placement={placement}
            slotProps={{
              popper: {
                disablePortal: true,
              },
              tooltip: {
                sx: {
                  bgcolor: 'rgba(33, 33, 33, 0.80)',
                  color: 'white',
                  padding: '10px',
                },
              },
              arrow: {
                sx: {
                  color: 'rgba(33, 33, 33, 0.80)',
                },
              },
            }}
          >
            <CustomChip
              onClick={() => setTooltipOpen((prev) => !prev)}
              sx={{
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                background: bgColor,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                lineHeight: '20px',
                color: theme.palette.common.black,
                '&:hover': {
                  background: bgColor,
                },
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: labelPadding,
                  lineHeight: '20px',
                },
              }}
              label={getInitials(input?.fullmonolithiceffect)}
              {...(!isEmpty(output) && {
                onDelete: () => {
                  onDelete(input?.fullmonolithiceffect)
                },
              })}
            />
          </Tooltip>
        </Box>
      </Box>
    </HtmlKonvaElement>
  )
}

export default KonvaCrown
